import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { UNIVERS } from "@/data/univers";

// Short per-tool descriptions revealed on hover (keyed by anchor, or route for ADN).
const DESC = {
  "thermometre-mental": { fr: "Prends la température de ton esprit.", en: "Take your mind's temperature.", es: "Toma la temperatura de tu mente.", nl: "Meet de temperatuur van je geest." },
  "la-curiosite": { fr: "Réveille ton envie d'apprendre.", en: "Awaken your urge to learn.", es: "Despierta tus ganas de aprender.", nl: "Wek je leergierigheid." },
  "chambre-noire": { fr: "Fais naître des images par l'IA.", en: "Bring images to life with AI.", es: "Crea imágenes con la IA.", nl: "Laat beelden ontstaan met AI." },
  "le-scribe": { fr: "Affine tes textes avec un allié.", en: "Refine your writing with an ally.", es: "Pule tus textos con un aliado.", nl: "Verfijn je teksten met een bondgenoot." },
  "le-temoin": { fr: "Analyse une image en un regard.", en: "Read any image at a glance.", es: "Analiza una imagen de un vistazo.", nl: "Analyseer een beeld in één oogopslag." },
  "l-aiguillon": { fr: "Une étincelle pour te lancer.", en: "A spark to get you going.", es: "Una chispa para arrancar.", nl: "Een vonk om te starten." },
  "terrier-lapin": { fr: "Plonge de lien en lien.", en: "Dive from link to link.", es: "Salta de enlace en enlace.", nl: "Duik van link naar link." },
  "boussole": { fr: "Des questions qui te surprennent.", en: "Questions that surprise you.", es: "Preguntas que te sorprenden.", nl: "Vragen die je verrassen." },
  "chasse-tresor": { fr: "Résous l'énigme, trouve le trésor.", en: "Solve the riddle, find the treasure.", es: "Resuelve el enigma, halla el tesoro.", nl: "Los het raadsel op, vind de schat." },
  "le-labo": { fr: "Expérimente, teste, découvre.", en: "Experiment, test, discover.", es: "Experimenta, prueba, descubre.", nl: "Experimenteer, test, ontdek." },
  "le-devineur": { fr: "L'IA devine ce à quoi tu penses.", en: "The AI guesses what you're thinking.", es: "La IA adivina lo que piensas.", nl: "De AI raadt wat je denkt." },
  "vrai-bluff": { fr: "Sauras-tu démasquer le faux ?", en: "Can you spot the bluff?", es: "¿Sabrás desenmascarar el farol?", nl: "Kun jij de bluf ontmaskeren?" },
  "jeux-logique": { fr: "Muscle ton raisonnement.", en: "Flex your reasoning.", es: "Ejercita tu razonamiento.", nl: "Traint je redeneervermogen." },
  "les-lexiques": { fr: "Les mots, décryptés simplement.", en: "Words, simply decoded.", es: "Las palabras, descifradas.", nl: "Woorden, eenvoudig ontcijferd." },
  "les-capsules": { fr: "Le savoir en petites doses.", en: "Knowledge in small doses.", es: "El saber en pequeñas dosis.", nl: "Kennis in kleine doses." },
  "l-academie": { fr: "Des parcours pour aller plus loin.", en: "Guided paths to go further.", es: "Rutas para ir más lejos.", nl: "Trajecten om verder te gaan." },
  "les-conferences": { fr: "Écoute les grands esprits.", en: "Listen to great minds.", es: "Escucha a las grandes mentes.", nl: "Luister naar grote geesten." },
  "les-debats": { fr: "Assiste à des débats d'idées.", en: "Watch ideas clash.", es: "Asiste a debates de ideas.", nl: "Woon ideeëndebatten bij." },
  "feu-de-camp": { fr: "Échange autour du feu.", en: "Share around the fire.", es: "Comparte alrededor del fuego.", nl: "Deel rond het vuur." },
  "/manifeste": { fr: "Notre serment anti-scolaire.", en: "Our anti-school pledge.", es: "Nuestro manifiesto anti-escolar.", nl: "Onze anti-school-belofte." },
  "/philosophie": { fr: "La pensée derrière le Cocoon.", en: "The thinking behind Cocoon.", es: "El pensamiento tras el Cocoon.", nl: "De gedachte achter Cocoon." },
  "/chemin-de-soie": { fr: "De la bulle à l'envol.", en: "From cocoon to flight.", es: "Del capullo al vuelo.", nl: "Van cocon tot vlucht." },
  "/spirale": { fr: "100 joueurs, 8 manches, un survivant.", en: "100 players, 8 rounds, one survivor.", es: "100 jugadores, 8 rondas, un superviviente.", nl: "100 spelers, 8 rondes, één overlevende." },
};

const HUB_COPY = {
  fr: { eyebrow: "Les univers du Cocoon", title: "Un monde, six territoires", sub: "Choisis ton terrain de jeu. Chaque univers regroupe ses propres outils, à explorer sans note ni pression.", explore: "Explorer" },
  en: { eyebrow: "The Cocoon universes", title: "One world, six territories", sub: "Pick your playground. Each universe gathers its own tools, to explore with no grades and no pressure.", explore: "Explore" },
  es: { eyebrow: "Los universos del Cocoon", title: "Un mundo, seis territorios", sub: "Elige tu terreno de juego. Cada universo reúne sus propias herramientas, para explorar sin notas ni presión.", explore: "Explorar" },
  nl: { eyebrow: "De Cocoon-universa", title: "Eén wereld, zes gebieden", sub: "Kies je speelterrein. Elk universum bundelt zijn eigen tools, te verkennen zonder cijfers of druk.", explore: "Verkennen" },
};

const NEW_LABEL = { fr: "Nouveau", en: "New", es: "Nuevo", nl: "Nieuw" };

const ToolLink = ({ tool, univers, lang }) => {
  const to = tool.to || `/univers/${univers.slug}#${tool.anchor}`;
  const { Icon } = tool;
  const key = tool.anchor || tool.to;
  const desc = (DESC[key] && (DESC[key][lang] || DESC[key].fr)) || "";
  return (
    <Link
      to={to}
      title={desc}
      data-testid={`tool-link-${tool.anchor || (tool.to || "").replace("/", "")}`}
      className="group/tool flex flex-col justify-center min-h-16 px-4 py-3 rounded-xl border border-primary/35 bg-white/[0.035] hover:border-primary hover:bg-primary/[0.07] transition-colors focus-visible:ring-2 focus-visible:ring-primary/70 outline-none"
      style={{ boxShadow: "inset 0 0 0 1px hsl(38 90% 55% / 0.06)" }}
    >
      <span className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-3 min-w-0">
          <span className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-primary/[0.08] border border-primary/40 text-primary/80 group-hover/tool:text-primary group-hover/tool:border-primary transition-colors">
            <Icon size={16} strokeWidth={1.8} />
          </span>
          <span className="text-sm text-foreground/70 group-hover/tool:text-primary transition-colors truncate">{tool.name}</span>
          {tool.isNew && (
            <span data-testid={`tool-new-${tool.anchor || (tool.to || "").replace("/", "")}`}
              className="flex-shrink-0 text-[9px] font-semibold uppercase tracking-[0.12em] px-2 py-0.5 rounded-full bg-primary text-background shadow-[0_0_12px_hsl(38_90%_55%/0.5)]">
              {NEW_LABEL[lang] || NEW_LABEL.fr}
            </span>
          )}
        </span>
        <ArrowRight size={15} className="text-primary opacity-0 -translate-x-2 group-hover/tool:opacity-100 group-hover/tool:translate-x-0 transition-all flex-shrink-0" />
      </span>
      {desc && (
        <span className="block overflow-hidden max-h-0 opacity-0 group-hover/tool:max-h-16 group-hover/tool:opacity-100 group-hover/tool:mt-2 transition-all duration-300 ease-out">
          <span className="block pl-12 text-xs leading-snug text-foreground/55" data-testid={`tool-desc-${tool.anchor || (tool.to || "").replace("/", "")}`}>{desc}</span>
        </span>
      )}
    </Link>
  );
};

const UniversBlock = ({ u, copy, index }) => {
  const split = u.layout === "split";
  const cardTo = u.slug ? `/univers/${u.slug}` : (u.tools[0]?.to || "/");
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, delay: (index % 3) * 0.08 }}
      className={`group/block relative overflow-hidden rounded-3xl border border-white/10 glass frame-pulse p-6 sm:p-8 lg:p-10 ${u.span} col-span-full flex ${split ? "flex-col lg:flex-row lg:items-stretch gap-8" : "flex-col gap-7"}`}
      data-testid={`univers-block-${u.id}`}
    >
      {/* signature corner glow */}
      <div aria-hidden className="absolute -top-24 -right-16 w-80 h-80 rounded-full pointer-events-none transition-transform duration-700 group-hover/block:scale-110"
        style={{ background: `radial-gradient(circle, hsl(${u.hue} 90% 55% / 0.22), transparent 62%)` }} />

      <div className={`relative z-10 flex flex-col ${split ? "lg:w-1/2 justify-center" : ""}`}>
        <span className="tracking-[0.28em] uppercase text-[11px] mb-3" style={{ color: `hsl(${u.hue} 80% 62% / 0.85)` }}>{u.label}</span>
        <h3 className="font-display text-3xl sm:text-4xl text-white leading-[1.05] pb-[0.06em]" data-testid={`univers-title-${u.id}`}>
          <span className="text-gradient-orange">{u.label}</span>
        </h3>
        <p className="mt-4 font-light text-foreground/60 leading-relaxed max-w-md">{u.promise[copy.lang] || u.promise.fr}</p>
        <Link
          to={cardTo}
          data-testid={`univers-cta-${u.id}`}
          className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary/90 hover:text-primary transition-colors w-fit"
        >
          {copy.explore} {u.label} <ArrowUpRight size={16} />
        </Link>
      </div>

      <div className={`relative z-10 flex flex-col gap-3 ${split ? "lg:w-1/2 lg:justify-center" : ""}`} data-testid={`univers-submenu-${u.id}`}>
        {u.tools.map((tool) => (
          <ToolLink key={tool.anchor || tool.to} tool={tool} univers={u} lang={copy.lang} />
        ))}
      </div>
    </motion.div>
  );
};

export const UniversHub = () => {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "fr").slice(0, 2);
  const copy = { ...(HUB_COPY[lang] || HUB_COPY.fr), lang };

  return (
    <section className="relative px-4 md:px-8 py-24 sm:py-28" data-testid="univers-hub">
      <div className="max-w-3xl mx-auto text-center mb-14">
        <p className="tracking-[0.3em] uppercase text-xs text-primary/80 mb-4">{copy.eyebrow}</p>
        <h2 className="font-display text-gradient-orange text-4xl sm:text-5xl lg:text-6xl leading-[1.05] pb-[0.06em]">{copy.title}</h2>
        <p className="mt-5 text-foreground/60 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">{copy.sub}</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-6 lg:gap-8 max-w-7xl mx-auto">
        {UNIVERS.map((u, i) => (
          <UniversBlock key={u.id} u={u} copy={copy} index={i} />
        ))}
      </div>
    </section>
  );
};
